import { TIdType } from '../config/custom-id-input-api-urls.config';
import { IHttpBaseResponse } from '../../utils/models/http-base-response.model';

/**
 * ============================================================
 * NID Verification Configuration
 * ============================================================
 * Enables the privacy → verify modal ownership flow
 * (same as customidinput) before the main data fetch proceeds.
 *
 * JSON usage:
 * "nidVerification": {
 *   "enabled": true,
 *   "idType": "NID",
 *   "endpointCode": "NIDAGETIDINFO",
 *   "usePrefetch": true,
 *   "nidTypeSource": "identificationDocumentType"
 * }
 */
export interface INidVerificationConfig {
  /** Enable/disable NID verification */
  enabled: boolean;

  /** The ID type: 'NID' | 'CHILD_ID' | 'NIN' */
  idType: TIdType;

  /** Endpoint code for the NIDA lookup (e.g. 'NIDAGETIDINFO') */
  endpointCode: string;

  /** Whether to prefetch the NID from the user's profile */
  usePrefetch?: boolean;

  /**
   * Form field key that holds the NID sub-type value.
   * Resolves to 'NATIONAL_ID' | 'FOREIGNER_ID' | 'REFUGEE_ID'.
   * If not provided, no sub-type filtering is applied.
   */
  nidTypeSource?: string;
}

/**
 * ============================================================
 * Pre-Fetch Check Configuration
 * ============================================================
 * A single pre-fetch check that runs before the main data fetch.
 * Multiple checks can be configured in an array and run sequentially;
 * the first failure stops the pipeline.
 *
 * JSON usage:
 * "preFetchChecks": [
 *   {
 *     "enabled": true,
 *     "url": "/integration/v1/fetch/sync",
 *     "endpointCode": "CHECK_PENDING_APPLICATION",
 *     "useBaseUrl": true,
 *     "method": "POST",
 *     "payloadKeyMappings": [...],
 *     "errorKey": "pendingApplicationExists",
 *     "errorMessage": "A pending application already exists.",
 *     "errorMessagePath": "error.message"
 *   }
 * ]
 */
export interface IPreFetchCheckConfig {
  /** Enable/disable this check */
  enabled: boolean;

  /** API URL to call */
  url: string;

  /** Endpoint code for the check */
  endpointCode: string;

  /** Whether to prepend the API gateway base URL. Defaults to false. */
  useBaseUrl?: boolean;

  /** HTTP method. Defaults to 'POST'. */
  method?: 'GET' | 'POST';

  /** Payload key mappings — same structure as the main fetch */
  payloadKeyMappings?: IPreFetchPayloadKeyMapping[];

  /** Whether to use multiple payload keys. Defaults to false. */
  useMultiplePayloadKeys?: boolean;

  /**
   * Error key set on the form control when this check fails.
   * Must match a key in validation.messages for display.
   */
  errorKey: string;

  /**
   * Fallback error message when the API response doesn't provide one
   * or errorMessagePath doesn't resolve.
   */
  errorMessage?: string;

  /**
   * Dot-notation path to extract the error message from the HTTP error body.
   * Tried in order: this path → 'error.message' → 'message' → errorMessage fallback.
   */
  errorMessagePath?: string;

  /**
   * Block on a successful HTTP response if a specific field has a specific value.
   * If not provided, only HTTP errors cause blocking.
   */
  blockOnSuccess?: IPreFetchBlockOnSuccessConfig;
}

/**
 * Configuration for blocking on a successful API response.
 *
 * The response from `/integration/v1/fetch/sync` wraps the inner result
 * as a JSON string inside `data.response`. The component will automatically
 * parse this string before evaluating any paths below.
 *
 * All dot-notation paths (responseField, messagePath, messageTranslationParams)
 * resolve against the PARSED inner response object.
 *
 * Message resolution order:
 * 1. messageTranslationKey + messageTranslationParams (translated + dynamic)
 * 2. messagePath (raw string from response)
 * 3. IPreFetchCheckConfig.errorMessage (static fallback)
 *
 * Example:
 * ```json
 * "blockOnSuccess": {
 *   "responseField": "data.unique",
 *   "value": false,
 *   "messageTranslationKey": "DUPLICATE_APPLICATION_PROPERTY",
 *   "messageTranslationParams": {
 *     "applicationNumber": "data.existingApplication.applicationNumber",
 *     "applicationState": "data.existingApplication.applicationState"
 *   }
 * }
 * ```
 */
export interface IPreFetchBlockOnSuccessConfig {
  /** Dot-notation path in the parsed inner response to check */
  responseField: string;

  /** Value that triggers blocking */
  value: unknown;

  /** Dot-notation path in the parsed inner response to extract a raw display message */
  messagePath?: string;

  /**
   * ngx-translate key for a translatable, parameterized message.
   * Used with `translateService.instant(key, params)` where params
   * are resolved via `messageTranslationParams`.
   *
   * Example: "DUPLICATE_APPLICATION_PROPERTY"
   * en.json: "A pending application ({{applicationNumber}}) already exists."
   */
  messageTranslationKey?: string;

  /**
   * Maps translation parameter names to dot-notation paths in the parsed
   * inner response. Each value is resolved via getNestedValue.
   *
   * Example:
   * { "applicationNumber": "data.existingApplication.applicationNumber" }
   */
  messageTranslationParams?: Record<string, string>;
}

/**
 * ============================================================
 * Pipeline Result Interfaces
 * ============================================================
 */

/** Result of the NID verification pipeline step */
export interface INidVerificationResult {
  passed: boolean;
  /** Overridden citizen ID (e.g. from prefetch) */
  citizenId?: string;
  /** Error key to set on the form control */
  errorKey?: string;
  /** Human-readable error message */
  errorMessage?: string;
}

/** Result of a single pre-fetch check or the entire pre-fetch check step */
export interface IPreFetchCheckResult {
  passed: boolean;
  /** Error key to set on the form control */
  errorKey?: string;
  /** Human-readable error message */
  errorMessage?: string;
}

/**
 * Payload key mapping for pre-fetch checks.
 */
export interface IPreFetchPayloadKeyMapping {
  /** Key name in the outgoing payload */
  key: string;

  /** If true, use staticValue instead of reading from a form field */
  isStatic: boolean;

  /** Form field key to read the value from (when isStatic is false) */
  sourceField?: string;

  /** Static value (when isStatic is true) */
  staticValue?: unknown;

  /** Whether to search in root form instead of scoped/repeater form */
  searchInRootForm?: boolean;
}

/**
 * ============================================================
 * API Request / Response Interfaces
 * ============================================================
 */

/** Request body sent to /integration/v1/fetch/sync */
export interface IIntegrationFetchSyncRequest {
  endpointCode: string;
  callerId: string;
  payload: Record<string, unknown>;
  requester: string;
}

/**
 * Response from /integration/v1/fetch/sync.
 * The inner response is a JSON-encoded string in `data.response`.
 */
export interface IIntegrationFetchSyncResponse extends IHttpBaseResponse {
  data: {
    response: string;
  };
}

/** Response from the NID privacy and verify endpoints */
export interface INidApiResponse extends IHttpBaseResponse {
  data?: Record<string, unknown>;
}

/**
 * Request body sent to the NID verify endpoint.
 * The shape varies by idType (NID vs CHILD_ID).
 */
export interface INidVerifyRequest {
  nid?: string;
  childId?: string;
  verifierType: string;
  verifierValue: string;
}

/** Error body shape from NID privacy/verify endpoints */
export interface INidApiErrorBody {
  message?: string;
  error?: { message?: string };
}

/** Returned in NidaResponse.data when the requester must verify ownership. */
export const USER_REQUIRE_VALIDATION = 'USER_REQUIRE_VALIDATION';

/** Privacy/verify may return 200 OK with this message in data (not only as HTTP error). */
export function isUserRequireValidationResponse(
  body:
    | INidApiResponse
    | INidApiErrorBody
    | Record<string, unknown>
    | null
    | undefined
): boolean {
  if (!body) {
    return false;
  }
  const record = body as Record<string, unknown>;
  const data = record['data'] as Record<string, unknown> | undefined;
  if (data?.['message'] === USER_REQUIRE_VALIDATION) {
    return true;
  }
  if (record['message'] === USER_REQUIRE_VALIDATION) {
    return true;
  }
  const nestedError = record['error'] as { message?: string } | undefined;
  return nestedError?.message === USER_REQUIRE_VALIDATION;
}

/** Profile data returned by the NID prefetch URL */
export interface INidPrefetchProfileData {
  accountVerified: boolean;
  identificationType: string;
  identificationNumber: string;
}

/**
 * Common error body shape from the integration gateway.
 * Index signature allows errorMessagePath to resolve arbitrary dot-notation paths.
 */
export interface IIntegrationErrorBody {
  message?: string;
  error?: { message?: string };
  [key: string]: unknown;
}
